import { TAX_ENTITY_TYPES } from '@/features/taxes/taxConstants'
import {
  evaluateQualification,
  type DisqualificationReason,
  type PracticeAnswers,
  type QualificationVerdict,
} from '@/features/user/signup/your-practice/qualification'
import { DQ_REASONS } from '@/reducers/auth/userReducer'

import { isAnswered } from '../questions/registry'
import { TAX_TREATMENTS } from './constants'
import type { IntakeAnswers } from './types'

const answerString = (
  answers: IntakeAnswers,
  key: string
): string | undefined => {
  const value = answers[key]
  return typeof value === 'string' ? value : undefined
}

/**
 * Only the c-corp entity type is read by any rule, so every other treatment maps to
 * 'other' rather than to the entity type it resembles. Naming them would assert facts
 * the intake never established — an `scorp` answer says how the practice is taxed, not
 * that it was an S corp for the whole year.
 */
const taxEntityType = (answers: IntakeAnswers): TAX_ENTITY_TYPES | 'other' =>
  answerString(answers, 'tax_treatment') === TAX_TREATMENTS.ccorp
    ? TAX_ENTITY_TYPES.form_1120
    : 'other'

/**
 * Whether the practice does not plan to hire, which legitimately skips the owners
 * question. It is revealed only for practices that plan to hire, so a solo practice is
 * never asked and must read as single-owner rather than as an unanswered question.
 */
const willNotHire = (answers: IntakeAnswers): boolean =>
  answerString(answers, 'hiring_plan') === 'solo'

const MULTIPLE_OWNERS_ANSWER: Record<string, string> = {
  yes: 'true',
  no: 'false',
}

const multipleOwnersAnswer = (answers: IntakeAnswers): string | null => {
  if (willNotHire(answers)) return 'false'
  return (
    MULTIPLE_OWNERS_ANSWER[
      answerString(answers, 'has_multiple_owners') ?? ''
    ] ?? null
  )
}

/**
 * Adapts the compliance intake's answers into the shape the shared signup rules read.
 * Questions this intake does not ask are passed unanswered, which every predicate but
 * one treats as qualifying; `hasMultipleOwners` is the exception and is filled in.
 */
export const complianceVerdictAnswers = (
  answers: IntakeAnswers
): PracticeAnswers => ({
  taxEntityType: taxEntityType(answers),
  businessEntity: undefined,
  hasMultipleOwners: multipleOwnersAnswer(answers),
  hasPhysicalInventory: null,
  fileInternationalTaxes: null,
  scorpEntireYear: null,
  numberOfEmployees: answerString(answers, 'practice_size') ?? '',
})

const VERDICT_QUESTION_KEYS = ['tax_treatment', 'practice_size'] as const

/**
 * Whether every question a verdict depends on has been collected. Narrower than the
 * shared `isAnswerSetComplete`, which requires four questions this intake never asks
 * and so would never be satisfied.
 */
export const hasVerdictInputs = (answers: IntakeAnswers): boolean =>
  VERDICT_QUESTION_KEYS.every((key) => isAnswered(answers[key])) &&
  (willNotHire(answers) || isAnswered(answers.has_multiple_owners))

/**
 * Null until the deciding questions are answered — callers render the ordinary report
 * rather than a rejection. Never persist the result: it must follow rule
 * changes, so it is recomputed on every render.
 */
export const complianceVerdict = (
  answers: IntakeAnswers
): QualificationVerdict | null =>
  hasVerdictInputs(answers)
    ? evaluateQualification(complianceVerdictAnswers(answers))
    : null

export const DQ_REASON_COPY: Record<DisqualificationReason, string> = {
  c_corp: 'Your practice is taxed as a C corporation',
  multiple_owners: 'Your practice has more than one owner',
  ten_or_more_employees: 'Your practice has 10 or more people',
  international_taxes: 'Your practice has international tax filings',
  physical_inventory: 'Your practice carries physical inventory',
  scorp_partial_year:
    'Your practice became an S corporation partway through the year',
}

/**
 * The clause naming a single reason, rendered as "… because it {verb} <b>{phrase}</b>."
 * Split so the phrase can be emphasized without markup in the copy table.
 *
 * Partial: a reason without a clause falls back to the multi-reason form below, whose
 * bullets name it instead, rather than to a clause written here to fill the gap.
 */
export const DQ_REASON_CLAUSE: Partial<
  Record<DisqualificationReason, { verb: string; phrase: string }>
> = {
  c_corp: { verb: 'involves', phrase: 'C Corporation tax requirements' },
  international_taxes: { verb: 'involves', phrase: 'international tax needs' },
  physical_inventory: {
    verb: 'involves',
    phrase: 'physical inventory and sales tax requirements',
  },
  multiple_owners: { verb: 'has', phrase: 'multiple practice owners' },
}

export const DQ_SENTENCE_LEAD =
  'Based on your answers, we’re not able to support your practice right now because it'

/**
 * Used when more than one reason applies. It ends at the colon rather than at "because it
 * involves", because the bullets it introduces are `DQ_REASON_COPY` entries — independent
 * sentences ("Your practice is taxed as a C corporation"), not the noun phrases that
 * "involves" would demand.
 */
export const DQ_MULTI_REASON_SENTENCE =
  'Based on your answers, we’re not able to support your practice right now:'

/**
 * Closes the rejection either way: after the clause, or after the list. It does not
 * offer a second look — the contact line at the foot of the card already does, and
 * saying it twice softened the rejection into something equivocal.
 */
export const DQ_SENTENCE_TAIL = 'Everything above is still yours to keep.'

/**
 * The "Learn more" answer behind a reason, shown as a text accordion under the reasons
 * list. Partial on purpose: only reasons whose answer the design actually supplies appear
 * here. These make claims about what Heard can and cannot take on, so a reason without
 * approved copy gets no explainer rather than one written to fill the gap.
 *
 * The answers are the ones signup's own disqualified page already ships, so a practice
 * turned away by the report reads what it would have read in signup.
 *
 * `ten_or_more_employees` has none, and it does reach this card: `evaluateQualification`
 * appends it to the `disqualified` reasons whenever another hard reason fires, so a C corp
 * with 10+ people lists both. Only a lone 10+ answer routes to the waitlist. The design
 * supplies an explainer for neither case — the waitlist frame has no Learn more section,
 * and no frame shows the combination — so its bullet stands on its own rather than
 * carrying copy invented here.
 */
export const DQ_REASON_EXPLAINER: Partial<
  Record<DisqualificationReason, { question: string; answer: string }>
> = {
  c_corp: {
    question: 'Why can’t you support C Corporations?',
    answer:
      'At the moment, Heard supports C Corporations (including Professional Corporations) only if they are taxed as S Corporations for the full tax year. We’re not currently able to support practices filing as C Corporations.',
  },
  international_taxes: {
    question: 'Why can’t you support international taxes?',
    answer:
      'At the moment, Heard focuses on U.S. based practices with U.S. only tax requirements. We want to be upfront so you’re not surprised later or missing the support you need.',
  },
  multiple_owners: {
    question: 'Why can’t you support multiple practice owners?',
    answer:
      'At the moment, Heard supports single-owner practices. Multi-owner structures often involve more complex tax and ownership considerations that we don’t currently support.',
  },
  physical_inventory: {
    question: 'Why can’t you support physical inventory?',
    answer:
      'At the moment, Heard supports service-based practices and doesn’t handle sales tax or inventory tracking for physical goods. We want to be upfront so you’re not missing critical support.',
  },
}

/**
 * The rules and the user record use different vocabularies. Partial-year S corps persist
 * as c-corp, matching what classic signup's disqualified page writes.
 */
export const DQ_REASON_TO_PERSISTED: Record<
  DisqualificationReason,
  DQ_REASONS
> = {
  c_corp: DQ_REASONS.CCORP,
  scorp_partial_year: DQ_REASONS.CCORP,
  multiple_owners: DQ_REASONS.HAS_MULTIPLE_OWNERS,
  ten_or_more_employees: DQ_REASONS.TOO_MANY_EMPLOYEES,
  international_taxes: DQ_REASONS.HAS_INTERNATIONAL_TAXES,
  physical_inventory: DQ_REASONS.HAS_PHYSICAL_INVENTORY,
}
